import PropTypes from 'prop-types';
import React, { useRef, useMemo } from 'react';
import classNames from 'classnames';
import { UncontrolledTooltip } from 'reactstrap';
import ResponsiveAvatar from '../../responsive_avatar';

const headerStateTexts = {
  paused: (
    <>
      <i className="fa-regular fa-circle-pause" /> Pitching Paused
    </>
  ),
  active: (
    <>
      <i className="fa-regular fa-clock" /> Updated Daily
    </>
  ),
};

function RankingIcon() {
  return (
    <div
      className="align-self-center"
      style={{
        backgroundColor: '#EF4444',
        borderRadius: '18px',
        color: '#FFF',
        fontSize: '16px',
        height: '36px',
        lineHeight: '36px',
        textAlign: 'center',
        width: '36px',
      }}
    >
      <i className="fa-regular fa-ranking-star" />
    </div>
  );
}

function Header({ pitchingStatus }) {
  return (
    <div className="d-flex mb-3">
      <RankingIcon />
      <div className="align-self-center fw-bold flex-fill px-2">Team Leaderboard</div>
      <div className="align-self-center font-size-14px text-secondary">{headerStateTexts[pitchingStatus]}</div>
    </div>
  );
}

Header.propTypes = {
  pitchingStatus: PropTypes.oneOf(['paused', 'active']).isRequired,
};

// Mimick what newer React already has. React Hook for generating unique IDs
// that can be passed to accessibility attributes. This is a side-effect in a
// side-effect free part of the code, so should this impede testing, it should
// be rewritten into something pure/determenistic & more complex. Or better yet,
// just use useId() from the actual newer React.
function useId() {
  const idRef = useRef();

  if (!idRef.current) {
    idRef.current = `id-${Math.random().toString().replace('.', '')}`;
  }

  return idRef.current;
}

function LadderDelta({ ladderDelta }) {
  const id = useId();
  if (ladderDelta) {
    const absDelta = Math.abs(ladderDelta);
    const tooltip = `${ladderDelta > 0 ? 'Up' : 'Down'} ${absDelta} ${absDelta === 1 ? 'position' : 'positions'} `
      + 'in the rank over the past 30 days vs. the prior 30-day period';

    return (
      <>
        <div className="font-size-14px text-end py-2 flex-shrink-0" style={{ width: '36px' }} id={id}>
          <span className="text-secondary">{absDelta} </span>
          <i
            className={classNames('fa-solid', {
              'fa-caret-up text-success': ladderDelta > 0,
              'fa-caret-down text-danger': ladderDelta < 0,
            })}
            style={{ marginLeft: '4px' }}
          />
          <UncontrolledTooltip placement="top" target={id} offset={[0, -10]}>
            {tooltip}
          </UncontrolledTooltip>
        </div>
      </>
    );
  }
  return <div className="py-2" style={{ width: '36px' }} />;
}

LadderDelta.propTypes = {
  ladderDelta: PropTypes.number.isRequired,
};

function PitchingPausedEntry({ unlockPitchesUrl }) {
  return (
    <li className="mt-3 pt-3" style={{ marginBottom: '-7px' }}>
      <div className="alert alert-periwinkle mb-0 text-center">
        <p className="fw-bold">
          Pitching paused. <a href={unlockPitchesUrl}> Unlock more pitches with a team plan.</a>
        </p>
        <p className="font-size-14px mb-0">Top teams pitch non-stop. Yours can too!</p>
      </div>
    </li>
  );
}

PitchingPausedEntry.propTypes = {
  unlockPitchesUrl: PropTypes.string.isRequired,
};

function ExpandToggle({ isExpanded, onToggle, hiddenCount }) {
  return (
    <li className="mt-3">
      <div className="text-center">
        <button className="btn btn-link btn-sm text-decoration-none p-0" type="button" onClick={onToggle}>
          {isExpanded ? (
            <>
              <i className="fa-regular fa-chevron-up me-1" />
              Show Less
            </>
          ) : (
            <>
              <i className="fa-regular fa-chevron-down me-1" />
              Show {hiddenCount} More
            </>
          )}
        </button>
      </div>
    </li>
  );
}

ExpandToggle.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  hiddenCount: PropTypes.number.isRequired,
};

function ProgressBar({ percent }) {
  const idSuffix = useId();
  const gradientId = `gradient-${idSuffix}`;
  const progressMaskId = `progress-mask-${idSuffix}`;

  const height = 10;
  const r = Math.floor(height / 2.0);

  return (
    <svg width="100%" height={height}>
      <rect x="0" y="0" width="100%" height={height} rx={r} ry={r} fill="#e0e0e0" />

      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#2b1d7c" />
          <stop offset="100%" stopColor="#533dd1" />
        </linearGradient>

        <mask id={progressMaskId}>
          <rect x="0" y="0" width={`${percent}%`} height={height} rx={r} ry={r} fill="white" />
        </mask>
      </defs>

      <rect
        x="0"
        y="0"
        width="100%"
        height={height}
        rx={r}
        ry={r}
        fill={`url(#${gradientId})`}
        mask={`url(#${progressMaskId})`}
      />
    </svg>
  );
}

ProgressBar.propTypes = {
  percent: PropTypes.number.isRequired,
};

function TeamQwotieBadge() {
  return (
    <span
      className="align-middle font-size-12px px-2 py-1 mx-3 text-white"
      style={{
        borderRadius: '3px',
        background: 'linear-gradient(90deg, #F75540 0%, #533DD1 100%)',
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="15"
        height="12"
        viewBox="0 0 15 12"
        fill="none"
        style={{ marginTop: '-3px', marginRight: '2px' }}
      >
        <path
          d="M1.13184 9.53896L5.43692 4.57157L5.71219 4.2549L8.01375
          6.55645L7.69708 6.83173L6.93127 7.49405V10.6359C6.93127 11.0023 6.6353
          11.2982 6.26895 11.2982C5.90261 11.2982 5.60664 11.0023 5.60664
          10.6359V8.64276L2.72969 11.1368C2.46683 11.3645 2.07358 11.35 1.82728
          11.1037L1.16496 10.4414C0.918661 10.1951 0.904172 9.80182 1.13184
          9.53896ZM6.27723 3.88441C6.21928 3.22623 6.44281 2.54736 6.94783
          2.04234L7.04511 1.94506L10.3236 5.22354L10.2263 5.32082C9.72129
          5.82583 9.04241 6.04937 8.38423 5.99141L6.27723 3.88441ZM7.51287
          1.4773L7.61015 1.38002C8.51463 0.47554 9.98415 0.47554 10.8886
          1.38002C11.7931 2.2845 11.7931 3.75402 10.8886 4.6585L10.7913
          4.75577L7.51287 1.4773ZM8.25591 8.31781C8.25591 8.16879 8.34905
          8.03425 8.48772 7.98251L10.2429 7.32433L10.901 5.56919C10.9528 5.43051
          11.0873 5.33737 11.2363 5.33737C11.3854 5.33737 11.5199 5.42844
          11.5716 5.56919L12.2298 7.32433L13.985 7.98251C14.1236 8.03425 14.2168
          8.16879 14.2168 8.31781C14.2168 8.46683 14.1257 8.60136 13.985
          8.65311L12.2298 9.31129L11.5716 11.0664C11.5199 11.2051 11.3854
          11.2982 11.2363 11.2982C11.0873 11.2982 10.9528 11.2072 10.901
          1.0664L10.2429 9.31129L8.48772 8.65311C8.34905 8.60136 8.25591
          8.46683 8.25591 8.31781Z"
          fill="white"
        />
      </svg>
      Team Qwotie
    </span>
  );
}

function getAvatarLetters(str) {
  if (!str || typeof str !== 'string' || str.trim() === '') {
    return 'PR';
  }

  const words = str
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);

  if (words.length === 0) {
    return 'PR';
  }

  const firstLetter = words[0][0].toUpperCase();
  const secondLetter = words.length > 1 ? words[1][0].toUpperCase() : '';

  return firstLetter + secondLetter;
}

function Entry({
 index, ladderDelta, totalPoints, profileImageUrl, profileName,
}) {
  const avatarLetters = getAvatarLetters(profileName);
  const isFirst = index === 0;

  return (
    <li
      className={classNames('d-flex align-items-center', {
        'border-top mt-3 pt-3': !isFirst,
      })}
    >
      <div className="fw-semibold">{index + 1}</div>
      <div
        className={`avatar avatar-framed avatar-sm mx-3 avatar-rank-${index + 1} flex-shrink-0`}
        style={{ width: '42px', height: '42px' }}
      >
        <ResponsiveAvatar
          avatarUrl={profileImageUrl}
          avatarLetters={avatarLetters}
          useInitials={true}
          className="avatar-responsive-cover avatar-letters-dicebear"
        />
      </div>
      <div className="flex-fill font-size-14px">
        <div className="d-flex flex-column">
          <div className="fw-bold">
            {profileName}
            {isFirst ? <TeamQwotieBadge /> : null}
          </div>
          <div className="mt-1 text-secondary">Impact Rating</div>
          <ProgressBar percent={totalPoints} />
        </div>
      </div>
      <LadderDelta ladderDelta={ladderDelta} />
    </li>
  );
}

Entry.propTypes = {
  index: PropTypes.number.isRequired,
  ladderDelta: PropTypes.number.isRequired,
  totalPoints: PropTypes.number.isRequired,
  profileImageUrl: PropTypes.string.isRequired,
  profileName: PropTypes.string.isRequired,
};

// Data processing functions
function preparePromoterAnalytics(rawEntries) {
  return rawEntries
    .toSorted((a, b) => {
      const pitchDiff = b.totalPoints - a.totalPoints;
      if (pitchDiff !== 0) return pitchDiff;
      return a.profileName.localeCompare(b.profileName);
    })
    .map((item, index) => ({ index, ...item }));
}

function addMissingProfiles(destination, reference) {
  const destinationProfileIds = new Set(destination.map((item) => item.profileId));

  const missingInDestination = reference
    .filter((prevItem) => !destinationProfileIds.has(prevItem.profileId))
    .map((prevItem) => ({
      profileId: prevItem.profileId,
      profileName: prevItem.profileName,
      totalPoints: 0,
    }));

  destination.push(...missingInDestination);
}

function calculateLadderDeltas(previous, current) {
  const previousIndicies = {};
  previous.forEach((item) => {
    previousIndicies[item.profileId] = item.index;
  });

  return current.map((item) => ({
    ...item,
    oldIndex: previousIndicies[item.profileId],
    ladderDelta: previousIndicies[item.profileId] - item.index,
  }));
}

function normalizePointScore(currentWithDeltas) {
  if (currentWithDeltas === []) {
    return [];
  }

  const [minPoints, maxPoints] = currentWithDeltas
    .map((e) => e.totalPoints)
    .reduce(([min, max], v) => [Math.min(min, v), Math.max(max, v)], [0, 0]);

  const normalized = currentWithDeltas.map(({ totalPoints, ...e }) => {
    const fraction = (totalPoints - minPoints) / (maxPoints - minPoints); // from 0.0 to 1.0
    const normalizedPoints = Math.round(fraction * 70 + 30); // from 30 to 100
    return {
      ...e,
      totalPoints: normalizedPoints,
    };
  });

  return normalized;
}

// Status-specific components
function LoadingView({ pitchingStatus }) {
  return (
    <div className="team-leaderboard card">
      <div className="card-body p-3 text-dark">
        <Header pitchingStatus={pitchingStatus} />
        <div className="text-center">Loading...</div>
      </div>
    </div>
  );
}

LoadingView.propTypes = {
  pitchingStatus: PropTypes.oneOf(['paused', 'active']).isRequired,
};

function ErrorView({ payload, onRetry }) {
  return (
    <div className="team-leaderboard card overflow-hidden">
      <div className="card-body p-3 text-dark">
        <Header pitchingStatus={payload.pitchingStatus} />
        <div className="text-center">
          <div className="alert alert-danger mb-1">
            <i className="fa-regular fa-triangle-exclamation mb-2" />
            <p className="mb-2">{payload?.message || 'Something went wrong'}</p>
            <button className="btn btn-sm btn-outline-danger" type="button" onClick={onRetry}>
              <i className="fa-regular fa-rotate-right me-1" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

ErrorView.propTypes = {
  payload: PropTypes.shape({
    pitchingStatus: PropTypes.oneOf(['paused', 'active']),
    message: PropTypes.string,
  }).isRequired,
  onRetry: PropTypes.func.isRequired,
};

function SuccessView({ payload, isExpanded, onToggleExpanded }) {
  const {
 rawPreviousData, rawCurrentData, unlockPitchesUrl, pitchingStatus,
} = payload || {};

  // Process raw data into display format
  const processedData = useMemo(() => {
    if (!rawCurrentData?.length && !rawPreviousData?.length) {
      return [];
    }

    // Clone the raw data to avoid mutations
    let previous = [...(rawPreviousData || [])];
    let current = [...(rawCurrentData || [])];

    // Ensure both datasets have all profiles (with 0 pitches for missing ones)
    addMissingProfiles(previous, current);
    addMissingProfiles(current, previous);

    // Sort and index both datasets
    previous = preparePromoterAnalytics(previous);
    current = preparePromoterAnalytics(current);

    // Calculate ladder position changes
    const currentWithDeltas = calculateLadderDeltas(previous, current);

    // Normalize point score from 0 to 100
    const normalizedScore = normalizePointScore(currentWithDeltas);

    return normalizedScore;
  }, [rawPreviousData, rawCurrentData]);

  // Compute derived values
  const isPaused = pitchingStatus === 'paused';
  const topCount = isPaused ? 3 : 5;
  const displayedEntries = isExpanded ? processedData : processedData.slice(0, topCount);
  const hasMore = processedData.length > topCount;
  const hiddenCount = processedData.length - topCount;

  return (
    <div className="team-leaderboard card overflow-hidden">
      <div className={classNames('card-body px-3 pt-3 pb-0 text-dark', { 'pb-0': isPaused, 'pb-3': !isPaused })}>
        <Header pitchingStatus={pitchingStatus} />
        {displayedEntries.length === 0 ? (
          <div className={classNames('text-center px-3 pt-2 fst-italic text-secondary', { 'pb-3': isPaused })}>
            <p>No leaderboard activity in the last 60 days.</p>
            <p>Jump back in, start pitching, and claim the top spot!</p>
          </div>
        ) : (
          <ul className="list-unstyled d-flex flex-column m-0">
            {displayedEntries.map((entry) => (
              <Entry
                index={entry.index}
                key={entry.profileId}
                ladderDelta={entry.ladderDelta}
                totalPoints={entry.totalPoints}
                profileImageUrl={entry.profileImageUrl}
                profileName={entry.profileName}
              />
            ))}
            {isPaused && <PitchingPausedEntry unlockPitchesUrl={unlockPitchesUrl} />}
            {!isPaused && hasMore && (
              <ExpandToggle isExpanded={isExpanded} onToggle={onToggleExpanded} hiddenCount={hiddenCount} />
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

SuccessView.propTypes = {
  payload: PropTypes.shape({
    rawPreviousData: PropTypes.array.isRequired,
    rawCurrentData: PropTypes.array.isRequired,
    unlockPitchesUrl: PropTypes.string.isRequired,
    pitchingStatus: PropTypes.oneOf(['paused', 'active']).isRequired,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggleExpanded: PropTypes.func.isRequired,
};

// Main routing component
function TeamLeaderboardView({ currentState, onRetry, onToggleExpanded }) {
  const { status, payload } = currentState;

  // Route to appropriate component based on status
  switch (status) {
    case 'loading':
      return <LoadingView payload={payload} />;

    case 'error':
      return <ErrorView payload={payload} onRetry={onRetry} />;

    case 'success':
    case 'success::expanded':
      return (
        <SuccessView
          payload={payload}
          isExpanded={status === 'success::expanded'}
          onToggleExpanded={onToggleExpanded}
        />
      );

    default:
      // Fallback for unknown status
      return <ErrorView payload={{ message: 'Unknown state' }} onRetry={onRetry} />;
  }
}

TeamLeaderboardView.propTypes = {
  currentState: PropTypes.shape({
    status: PropTypes.oneOf(['loading', 'error', 'success', 'success::expanded']).isRequired,
    payload: PropTypes.shape({
      // always present
      pitchingStatus: PropTypes.oneOf(['paused', 'active']).isRequired,

      // for "error" and default
      message: PropTypes.string,

      // for "success" / "success::expanded"
      unlockPitchesUrl: PropTypes.string,
      rawPreviousData: PropTypes.arrayOf(
        PropTypes.shape({
          profileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          profileName: PropTypes.string.isRequired,
          totalPoints: PropTypes.number.isRequired,
          profileImageUrl: PropTypes.string,
        }),
      ),
      rawCurrentData: PropTypes.arrayOf(
        PropTypes.shape({
          profileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          profileName: PropTypes.string.isRequired,
          totalPoints: PropTypes.number.isRequired,
          profileImageUrl: PropTypes.string,
        }),
      ),
    }).isRequired,
  }).isRequired,

  onRetry: PropTypes.func.isRequired,
  onToggleExpanded: PropTypes.func.isRequired,
};

export default TeamLeaderboardView;
